import { reactive, markRaw } from "vue";
import { deleteEquipmentListContent, reorderEquipmentListContent } from "@/services/projectService";
import AddGroupModal from "./modals/AddGroupModal.vue";
import AddAssetModal from "./modals/AddAssetModal.vue";
import AddCrewModal from "./modals/AddCrewModal.vue";
import AddLogisticsModal from "./modals/AddLogisticsModal.vue";
import AddSubhiresModal from "./modals/AddSubhiresModal.vue";
import ManageSubhiresModal from "./modals/ManageSubhiresModal.vue";
import ManageTransportModal from "./modals/ManageTransportModal.vue";
import EditSubhireModal from "./modals/EditSubhireModal.vue";
import GenerateSubhireDocumentModal from "./modals/GenerateSubhireDocumentModal.vue";
import RequestQuotesModal from "./modals/RequestQuotesModal.vue";
import MoveEquipmentModal from "./modals/MoveEquipmentModal.vue";
import AddTemporaryModal from "./modals/AddTemporaryModal.vue";
import AddAccessoryModal from "./modals/AddAccessoryModal.vue";
import GroupPriceModal from "./modals/GroupPriceModal.vue";
import GroupColourModal from "./modals/GroupColourModal.vue";
import NotesModal from "./modals/NotesModal.vue";

export const projectStore = reactive({
    // UI State Only - No Data Storage
    isModalVisible: false,
    currentModalComponent: null,
    modalHeader: "Modal",
    modalWidth: "450px",
    currentDraggableItemId: null,
    currentLocalAssetId: null,
    currentModalProps: null,
    
    // Route Context - Store these for easy access across components
    currentProjectId: null,
    currentEquipmentListId: null,
    refreshFunction: null,
    refreshSubhiresCheck: null, // Function to refresh subhires tab visibility
    updateContentItemInPlace: null, // (contentId, patch) => void - patch item in list without full refresh

    // Crew Planning: crew type content IDs that have at least one assignment with crew assigned (filled/requested/confirmed)
    crewTypeIdsWithAssignedCrew: [],
    setCrewTypeIdsWithAssignedCrew(ids) {
        this.crewTypeIdsWithAssignedCrew = Array.isArray(ids) ? ids : [];
    },

    // Modal management
    openDialog(type, id, localAssetId = null, additionalProps = null) {
        const modalConfig = {
            group: { component: markRaw(AddGroupModal), header: "Add Group" },
            asset: { component: markRaw(AddAssetModal), header: "Add Asset", width: "90vw" },
            crew: { component: markRaw(AddCrewModal), header: "Add Crew", width: "90vw" },
            logistics: { component: markRaw(AddLogisticsModal), header: "Add Logistics", width: "90vw" },
            subhire: { component: markRaw(AddSubhiresModal), header: "Add Subhire" },
            manageSubhires: { component: markRaw(ManageSubhiresModal), header: "Manage Subhires", width: "90vw" },
            manageTransport: { component: markRaw(ManageTransportModal), header: "Manage Transport", width: "500px" },
            editSubhire: { component: markRaw(EditSubhireModal), header: "Edit Subhire", width: "90vw" },
            generateSubhireDocument: { component: markRaw(GenerateSubhireDocumentModal), header: "Generate Document", width: "600px" },
            requestQuotes: { component: markRaw(RequestQuotesModal), header: "Request availability & quotes", width: "560px" },
            moveEquipment: { component: markRaw(MoveEquipmentModal), header: "Move Equipment", width: "600px" },
            temporary: { component: markRaw(AddTemporaryModal), header: "Add Temporary Item" },
                accessory: { component: markRaw(AddAccessoryModal), header: "Add Accessory", width: "90vw" },
                groupPrice: { component: markRaw(GroupPriceModal), header: "Set Group Price", width: "500px" },
                groupColour: { component: markRaw(GroupColourModal), header: "Change Group Colour", width: "400px" },
                notes: { component: markRaw(NotesModal), header: "Equipment Notes", width: "600px" },
        };

        const config = modalConfig[type];
        if (config) {
            this.currentModalComponent = config.component;
            this.modalHeader = config.header;
            this.modalWidth = config.width || "450px"; // Default width if not specified
            this.isModalVisible = true;
            this.currentDraggableItemId = id;
            this.currentLocalAssetId = localAssetId; // Store the local asset ID
            this.currentModalProps = additionalProps; // Store additional props
        } else {
            console.warn(`Unsupported dialog type: ${type}`);
        }
    },

    // Method to show toast notifications (placeholder - not used)
    showToast() {
        // This method is kept for compatibility but not used
        // Real toasts are handled by PrimeVue toast service
    },

    // Method to close modal
    closeModal() {
        this.isModalVisible = false;
        this.currentModalComponent = null;
        this.currentDraggableItemId = null;
        this.currentLocalAssetId = null;
        this.currentModalProps = null;
    },

    // Method to delete content item
    async deleteContentItem(contentId) {
        try {
            if (!this.currentProjectId || !this.currentEquipmentListId) {
                throw new Error('Project ID or Equipment List ID not found');
            }

            await deleteEquipmentListContent(this.currentProjectId, this.currentEquipmentListId, contentId);
            
            // Refresh the equipment list content
            if (this.refreshFunction) {
                this.refreshFunction();
            }
            
        } catch (error) {
            console.error('Error deleting content item:', error);
            throw error;
        }
    },

    // Method to reorder content items
    async reorderContentItems(tasks) {
        try {
            if (!this.currentProjectId || !this.currentEquipmentListId) {
                throw new Error('Project ID or Equipment List ID not found');
            }

            // Build reorder data from the hierarchical tasks array
            // We need to flatten the hierarchy to get all items (assets and accessories) in order
            const flattenedItems = this.flattenTasksHierarchy(tasks);
            
            const reorderData = {
                items: flattenedItems.map((item, index) => ({
                    id: parseInt(item.id),
                    sort_order: index + 1,
                    parent_id: item.parent_id || null
                }))
            };

            await reorderEquipmentListContent(this.currentProjectId, this.currentEquipmentListId, reorderData);
        } catch (error) {
            console.error('Error reordering content items:', error);
            
            // Refresh on error to ensure UI is consistent with backend
            if (this.refreshFunction) {
                this.refreshFunction();
            }
            
            throw error;
        }
    },

    // Helper method to flatten hierarchical tasks structure
    flattenTasksHierarchy(tasks) {
        const flattened = [];
        
        tasks.forEach(task => {
            // Add the main task (asset/group) - PRESERVE TYPE
            // Groups at top level have parent_id: null
            // Items within groups have parent_id: group.id
            flattened.push({
                id: task.id,
                parent_id: task.parent_id || null,
                type: task.type // CRITICAL: Preserve the type
            });
            
            // Add its children (items/accessories) if any - PRESERVE TYPE
            if (task.tasks && task.tasks.length > 0) {
                task.tasks.forEach(child => {
                    // Items within groups should have parent_id: group.id
                    // Accessories within items should have parent_id: item.id
                    flattened.push({
                        id: child.id,
                        parent_id: child.parent_id || task.id, // Set parent to the main task
                        type: child.type // CRITICAL: Preserve the type
                    });
                });
            }
        });
        
        return flattened;
    }
});